import { Grid } from "@mui/material";
import * as React from "react";
import { ApplicationPermissionType } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "../../../framework/appUtils";
import * as appReportService from "../../../models/services/appReportService";
import { ISessionState } from "../../../models/session/sessionState";
import { IUnitModuleNode } from "../../../models/unit/unitModuleNode";
import { IUnitNodePermissions } from "../../../models/unit/unitNodePermissions";
import { Dialog } from "../../framework/Dialog/Dialog";
import { GridRow } from "../../framework/Grid/GridRow";
import { IPageNavMenuItem, PageNavMenuItems } from "../../framework/PageNav/PageNavMenuItems";
import { UnitModuleSettings } from "../../unit/Unit/UnitModuleSettings";
import { UnitPermissionSettingsNew } from "source/components/unit/Unit/UnitPermissionSettingsNew";
import { AppReportTemplateGrid } from "./AppReportTemplateGrid";

/* eslint-disable no-unused-vars */
export const enum AppReportSettingsDialogMenuItem {
    Units = "1",
    Permissions = "2",
    // TaskStates = "3",
    // TaskPriorities = "4",
    CompanyReportTemplates = "5",
    // DefaultTaskStates = "6",
    // DefaultTaskPriorities = "7",
    DefaultReportTemplates = "8"
}
/* eslint-enable no-unused-vars */

export interface IAppReportSettingsDialogProp {
    sessionState: ISessionState;
    companyId: string;
    classes?: string;
    settingsActions: IPageNavMenuItem[];
    onClose: () => void;
}

export interface IAppReportSettingsDialogState {
    // Units
    unitModuleNode: IUnitModuleNode;
    // Permissions
    unitNodePermissions: IUnitNodePermissions;
    showTaskTypesByUnitsDialog: boolean;
    activeMenuItem: AppReportSettingsDialogMenuItem;
}

export class AppReportSettingsDialog extends React.Component<IAppReportSettingsDialogProp, IAppReportSettingsDialogState> {
    private unitsRef = React.createRef<UnitModuleSettings>();
    private permissionsRef = React.createRef<UnitPermissionSettingsNew>();

    constructor(props: IAppReportSettingsDialogProp) {
        super(props);
        this.state = {
            unitModuleNode: null,
            unitNodePermissions: null,
            showTaskTypesByUnitsDialog: false,
            activeMenuItem: props.settingsActions.findIndex(item => item.id === AppReportSettingsDialogMenuItem.Units) !== -1
                ? AppReportSettingsDialogMenuItem.Units
                : AppReportSettingsDialogMenuItem.Permissions,
        };
    }

    async componentDidMount(): Promise<void> {
        const state = this.state;
        if (state.activeMenuItem === AppReportSettingsDialogMenuItem.Units) {
            await this.loadUnits();
        }
    }

    // #region Units
    onUnitModuleSettingsCancel = async (): Promise<boolean> => {
        return await this.unitsRef.current.handleCancel(false);
    };

    loadUnits = async () => {
        const result = await AppUtils.callService(() => appReportService.getUnits(this.props.companyId), false);
        if (!result) return;
        this.setState({
            unitModuleNode: result.unitModuleNode
        });
    };

    handleUnitModuleSettingsSave = async (obj: any) => {
        return await appReportService.saveUnits(obj);
    };
    // #endregion Units

    // #region Permissions
    onUnitPermissionSettingsCancel = async (): Promise<boolean> => {
        return await this.permissionsRef.current.handleCancel(false);
    };

    loadPermissions = async () => {
        const result = await AppUtils.callService(() => appReportService.getUnitNodePermissions(this.props.companyId), false);
        if (!result) return;
        this.setState({
            unitNodePermissions: result
        });
    };

    handleUnitPermissionSettingsSave = async (obj: any) => {
        const result = await appReportService.saveUnitNodePermissions(obj);
        this.loadPermissions();
        return result;
    };
    // #endregion Permissions

    handleSubViewOk = (_closeDialog: boolean) => {
    };

    handleToggleActiveMenuItem = async (menuItem: AppReportSettingsDialogMenuItem) => {
        const state = this.state;
        if (state.activeMenuItem === menuItem) return;
        //Check current tab cancel
        if (state.activeMenuItem === AppReportSettingsDialogMenuItem.Units) {
            if (!await this.onUnitModuleSettingsCancel()) return;
        } else if (state.activeMenuItem === AppReportSettingsDialogMenuItem.Permissions) {
            if (!await this.onUnitPermissionSettingsCancel()) return;
        }
        //Load new tab data
        if (menuItem === AppReportSettingsDialogMenuItem.Units) {
            await this.loadUnits();
        } else if (menuItem === AppReportSettingsDialogMenuItem.Permissions) {
            await this.loadPermissions();
        }
        //Set new tab active
        this.setState({
            activeMenuItem: menuItem
        });
    };

    handleShowTaskTypesByUnitsDialog = () => {
        this.setState({
            showTaskTypesByUnitsDialog: true
        });
    };

    handleCloseTaskTypesByUnitsDialog = () => {
        this.setState({
            showTaskTypesByUnitsDialog: false
        });
    };

    handleCancel = async (closeDialog: boolean) => {
        if (!closeDialog) return;
        this.props.onClose();
    };

    render() {
        const props = this.props;
        const state = this.state;
        return (
            <>
                <Dialog
                    id="appReportSettingsDialog"
                    title={Translations.Settings}
                    maxWidth="lg"
                    maxHeight="100%"
                    onClose={() => this.handleCancel(true)}
                    body={
                        <GridRow flexGrow={2}>
                            <Grid item sm={3}>
                                <PageNavMenuItems
                                    activeId={state.activeMenuItem}
                                    items={props.settingsActions}
                                    onToggleMenuItem={this.handleToggleActiveMenuItem}
                                />
                            </Grid>
                            <Grid item sm={9} display="flex" flexDirection="column" flexGrow={2}>
                                {(state.activeMenuItem === AppReportSettingsDialogMenuItem.Units) && (
                                    <UnitModuleSettings
                                        ref={this.unitsRef}
                                        infoText={Translations.AppReportModuleInfoUnits}
                                        companyId={props.companyId}
                                        sessionState={props.sessionState}
                                        applicationPermissionType={ApplicationPermissionType.AppReport}
                                        unitModuleNode={state.unitModuleNode}
                                        onCancel={this.handleCancel}
                                        onOk={this.handleSubViewOk}
                                        onSave={this.handleUnitModuleSettingsSave}
                                    />
                                )}
                                {state.activeMenuItem === AppReportSettingsDialogMenuItem.Permissions && (
                                    <UnitPermissionSettingsNew
                                        ref={this.permissionsRef}
                                        infoText={Translations.AppReportModuleInfoPermissions}
                                        companyId={props.companyId}
                                        unitNodePermissions={state.unitNodePermissions}
                                        cardView={true}
                                        onSave={this.handleUnitPermissionSettingsSave}
                                        onOk={this.handleSubViewOk}
                                        onCancel={this.handleCancel}
                                    />
                                )}
                                {(state.activeMenuItem === AppReportSettingsDialogMenuItem.CompanyReportTemplates || state.activeMenuItem === AppReportSettingsDialogMenuItem.DefaultReportTemplates) &&
                                    <AppReportTemplateGrid
                                        companyId={state.activeMenuItem === AppReportSettingsDialogMenuItem.CompanyReportTemplates ? props.companyId : null}
                                        sessionState={props.sessionState}
                                    />
                                }
                            </Grid>
                        </GridRow>
                    }
                    buttons={
                        state.activeMenuItem !== AppReportSettingsDialogMenuItem.Units && state.activeMenuItem !== AppReportSettingsDialogMenuItem.Permissions
                            ? [{ title: Translations.Close, variant: "outlined", onClick: () => this.handleCancel(true) }]
                            // ? (state.activeMenuItem === TaskSettingsDialogMenuItem.CompanyTaskTypes
                            //     ? [{ title: Translations.TaskTypesByUnits, variant: "confirmation", margin: "0 auto 0 25.4%", onClick: () => this.handleShowTaskTypesByUnitsDialog() }, { title: Translations.Close, variant: "outlined", onClick: () => this.handleCancel(true) }]
                            //     : [{ title: Translations.Close, variant: "outlined", onClick: () => this.handleCancel(true) }]
                            // )
                            : null
                    }
                />
                {/* {state.showTaskTypesByUnitsDialog &&
                    <TaskTypesByUnitsDialog
                        companyId={props.companyId}
                        onCancel={this.handleCloseTaskTypesByUnitsDialog}
                    />
                } */}
            </>
        );
    }
}